"use client";
import { Logo } from "@areahub/icons";
import { Box, Divider, Flex, Grid, Stack, Text } from "@chakra-ui/react";
import { Link } from "@ui/components/link/link";
import { Copyright } from "./copyright";
import { FeedbackTrigger } from "./feedback";
import { SocialMediaLinks } from "./social-media-links";
import { useAnalytics } from "@areahub/adapters/src/analytics/analytics.client";
import { isBusinesses } from "@ui/utils";
import { usePathname } from "next/navigation";

export type Footer_T = {
  linkGroups?: {
    title: string;
    links: any[];
  }[];
  bottomLinks?: any[];
  copyright?: string | null;
};

const Footer = (props: Footer_T) => {
  const { linkGroups, bottomLinks, copyright } = props;
  const analytics = useAnalytics();
  const pathname = usePathname();
  return (
    <Box
      as="footer"
      pos={"relative"}
      bg="blue.50"
      py="36px"
      px={{ base: "20px", md: "40px" }}
      zIndex={9}
    >
      <Box
        mx="auto"
        maxW={{ base: "xl", md: "7xl" }}
        role="contentinfo"
        fontSize={{ base: "xs", md: "sm" }}
        borderBottomRadius="0"
      >
        <Stack spacing="30px" divider={<Divider />} mx="auto">
          <Stack
            direction={{ base: "column", lg: "row" }}
            alignItems="center"
            justifyContent="space-between"
            spacing={{ base: "30px", md: "50px" }}
          >
            {/** The areahub logo */}
            <Box as={Logo} h="10" />
            {/** The grid of links */}
            <Grid
              gridAutoFlow="column"
              mx="auto"
              textAlign={{ base: "center", md: "left" }}
              templateColumns="repeat(3, 1fr)"
              columnGap={{ base: "10px", md: "50px" }}
              rowGap="10px"
            >
              {linkGroups?.map(({ title, links }, idx) => {
                return (
                  <Flex
                    key={`footer-link-group-${idx}`}
                    flexDir={"column"}
                    gap={4}
                  >
                    <Text fontWeight={"bold"}>{title}</Text>
                    {links.map(({ href, isExternal, label }, idx) => (
                      <>
                        {label === "Log In" ? (
                          isBusinesses(pathname as string) ? (
                            <Link
                              key={idx}
                              href={
                                "https://enterprise.areahub.com/api/auth/login"
                              }
                              isExternal={isExternal}
                              onClick={() => {
                                analytics.track(
                                  "User clicked on a link in the footer",
                                  {
                                    ui: "Footer",
                                    requestedUrl:
                                      "https://enterprise.areahub.com/api/auth/login",
                                  },
                                );
                              }}
                            >
                              {label}
                            </Link>
                          ) : (
                            <Link
                              key={idx}
                              href={"https://premium.areahub.com"}
                              isExternal={isExternal}
                              onClick={() => {
                                analytics.track(
                                  "User clicked on a link in the footer",
                                  {
                                    ui: "Footer",
                                    requestedUrl: "https://premium.areahub.com",
                                  },
                                );
                              }}
                            >
                              {label}
                            </Link>
                          )
                        ) : (
                          <Link
                            key={idx}
                            href={href}
                            isExternal={isExternal}
                            onClick={() => {
                              analytics.track(
                                "User clicked on a link in the footer",
                                {
                                  ui: "Footer",
                                  requestedUrl: href,
                                },
                              );
                            }}
                          >
                            {label}
                          </Link>
                        )}
                      </>
                    ))}
                  </Flex>
                );
              })}
            </Grid>
            {/** 3rd section of stack */}
            <Stack flexDir="column" gap="10px">
              {/** The social media links */}
              <SocialMediaLinks />
              {/** The "send feedback" element */}
              <FeedbackTrigger />
            </Stack>
          </Stack>
          <Stack
            direction={{ base: "column", md: "row" }}
            justifyContent="space-between"
            alignItems="center"
            gap="15px"
          >
            <Copyright>{copyright}</Copyright>
            <Flex
              flexDir="row"
              justifyContent="space-evenly"
              textAlign="center"
              gap={{ base: "15px", md: "40px" }}
              overflow="visible"
              flexWrap="wrap"
              justifyItems="center"
            >
              {bottomLinks?.map(({ label, href, isExternal }, idx) => {
                return (
                  <Link
                    key={idx}
                    href={href}
                    isExternal={isExternal}
                    onClick={() => {
                      analytics.track("User clicked on a link in the footer", {
                        ui: "Footer",
                        requestedUrl: href,
                      });
                    }}
                  >
                    {label}
                  </Link>
                );
              })}
            </Flex>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default Footer;
